import { supabase } from '../lib/supabase';
import { logger } from '../utils/logger';

export interface MedicationStats {
  taken: number;
  taken_late: number;
  missed: number;
  points: number;
  adherence: number;
}

async function getUnseenCompleted(memberId: string): Promise<any[]> {
  try {
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD

    // 1) Buscar medicações concluídas
    const { data: meds, error: medsError } = await supabase
      .from('medications_v2' as any)
      .select('id')
      .eq('member_id', memberId)
      .eq('is_active', false)
      .or(`end_date.is.null,end_date.lte.${today}`)
      .order('updated_at', { ascending: false });

    if (medsError) {
      logger.error('[TreatmentSummaryService] Erro ao buscar medicações concluídas:', medsError);
      return [];
    }

    if (!meds || meds.length === 0) return [];

    const ids = meds.map((m: any) => m.id);

    // 2) Buscar já exibidos
    const { data: shown, error: shownError } = await supabase
      .from('treatment_summary_views' as any)
      .select('medication_id')
      .eq('member_id', memberId)
      .in('medication_id', ids);

    if (shownError) {
      // Não bloquear por erro; apenas logar
      logger.warn('[TreatmentSummaryService] Falha ao buscar treatment_summary_views, seguindo sem filtro', shownError);
      return meds;
    }

    const shownSet = new Set((shown || []).map((r: any) => r.medication_id));
    return meds.filter((m: any) => !shownSet.has(m.id));
  } catch (error) {
    logger.error('[TreatmentSummaryService] Erro inesperado em getUnseenCompleted:', error);
    return [];
  }
}

async function getMedicationStats(medicationId: string): Promise<MedicationStats> {
  try {
    const { data, error } = await supabase
      .from('medication_logs_v2' as any)
      .select('taken, taken_late, missed, points_earned, adherence_percentage_unified')
      .eq('medication_id', medicationId);

    if (error) {
      logger.error('[TreatmentSummaryService] Erro ao buscar logs:', error);
      return { taken: 0, taken_late: 0, missed: 0, points: 0, adherence: 0 };
    }

    let taken = 0;
    let taken_late = 0;
    let missed = 0;
    let points = 0;
    let adherence = 0;

    (data || []).forEach((row: any) => {
      taken += Number(row.taken) || 0;
      taken_late += Number(row.taken_late) || 0;
      missed += Number(row.missed) || 0;
      points += Number(row.points_earned) || 0;
      const perc = Number(row.adherence_percentage_unified) || 0;
      if (perc > adherence) adherence = perc; // usar MAX
    });

    return { taken, taken_late, missed, points, adherence };
  } catch (error) {
    logger.error('[TreatmentSummaryService] Erro inesperado em getMedicationStats:', error);
    return { taken: 0, taken_late: 0, missed: 0, points: 0, adherence: 0 };
  }
}

async function getMedication(medicationId: string): Promise<any | null> {
  try {
    const { data, error } = await supabase
      .from('medications_v2' as any)
      .select(`
        id,
        member_id,
        remedy_name,
        potency,
        dosage_type,
        quantity,
        times,
        frequency,
        start_date,
        end_date,
        usage_days,
        pause_days,
        total_period_months,
        notes,
        is_active
      `)
      .eq('id', medicationId)
      .single();

    if (error) {
      logger.error('[TreatmentSummaryService] Erro ao buscar medicação:', error);
      return null;
    }

    return data;
  } catch (error) {
    logger.error('[TreatmentSummaryService] Erro inesperado em getMedication:', error);
    return null;
  }
}

async function markSummaryShown(memberId: string, medicationId: string): Promise<void> {
  try {
    // upsert idempotente
    const { error } = await supabase
      .from('treatment_summary_views' as any)
      .upsert({ member_id: memberId, medication_id: medicationId }, { onConflict: 'member_id,medication_id', ignoreDuplicates: true } as any);

    if (error) {
      // se der violação de unique, ignorar
      const message = (error as any)?.message || '';
      if (!/duplicate|unique/i.test(message)) {
        logger.warn('[TreatmentSummaryService] Falha ao marcar summary como exibido:', error);
      }
    }
  } catch (error) {
    logger.warn('[TreatmentSummaryService] Erro inesperado ao marcar summary mostrado:', error);
  }
}

const TreatmentSummaryService = {
  getUnseenCompleted,
  getMedicationStats,
  getMedication,
  markSummaryShown,
};

export default TreatmentSummaryService;
